import { useCallback } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { isArray } from 'lodash-es'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import {
  getGetMyOrganisationsQueryKey,
  getMyOrganisations,
} from '@api/my-organisation/my-organisation'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { organisationLabel } from '@utils'
import { getProject } from 'src/state/queries/project'
import { organisationToAdopt } from './utils'

/**
 * Make the project's organisation the active one when a project is opened.
 *
 * Access to a project follows its own organisation, not the active one, so a
 * link to a project of another organisation opens fine -- but the switcher,
 * the library and the templates would keep showing the organisation the user
 * came from, suggesting the project belongs to it. Switching on open keeps
 * both in agreement.
 *
 * Only on the project's first load: later refetches (e.g. after the user picks
 * another organisation in the switcher) must not fight that choice. Only into
 * organisations the user is a member of; for any other the backend refuses the
 * project anyway.
 *
 * Returns an `onSuccess` handler for the editor's project query.
 */
const useAdoptProjectOrganisation = (
  projectId: string | undefined,
): ((project: ProjectData) => void) => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useCallback(
    async (project: ProjectData) => {
      if (!projectId) return
      if (queryClient.getQueryState(getProject.getKey(projectId))?.dataUpdateCount !== 1) return

      const { activeOrganisationGuid, setActiveOrganisationGuid } = useAppStore.getState()
      if (project.organization_guid === activeOrganisationGuid) return

      let organisations: MyOrganisationDTO[]
      try {
        const memberships = await queryClient.fetchQuery({
          queryKey: getGetMyOrganisationsQueryKey(),
          queryFn: ({ signal }) => getMyOrganisations(signal),
          staleTime: Infinity,
        })
        organisations = memberships.organisations
      } catch (error) {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Organisationen'),
          { variant: 'error' },
        )
        return
      }

      const target = organisationToAdopt(
        organisations,
        activeOrganisationGuid,
        project.organization_guid,
      )
      if (!target) return

      setActiveOrganisationGuid(target.guid)
      // Organisation-scoped data (library, templates, project list) was fetched
      // for the organisation just left. The project's own queries answer the
      // same regardless of the active organisation, so they are kept.
      queryClient.invalidateQueries({
        predicate: query => !(isArray(query.queryKey) && query.queryKey.includes(projectId)),
      })
      enqueueSnackbar(`Organisation gewechselt: ${organisationLabel(target)}`, {
        variant: 'info',
      })
    },
    [projectId, queryClient, enqueueSnackbar],
  )
}

export default useAdoptProjectOrganisation
